/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { timersProxyFactory } from './timersProxy.js';
import timerModes from './timerModes.js';
import { getTimersStore } from './timersStore.js';
import { buildInitialTimersStoreData } from './initialTimers.js';
import { cloneDeep } from 'lodash';
import { showNotification } from '@oat-sa-private/ui-components';
import { __, humanizeTime } from '@oat-sa-private/ui-core';

const DEFAULT_WARNING_LEVEL = 'test';
const TEST_TIMER_KEY = 'test:global';
const visibleTimerLevels = [DEFAULT_WARNING_LEVEL, 'testPart', 'section', 'item'];
const defaultWarningConfig = {
    levels: {
        test: {
            thresholdsInMs: []
        }
    },
    notificationTimeout: 5000
};
const warningLevels = ['testPart', 'section', 'item'];

const defaultWarningMessages = {
    levels: {
        test: {
            getMessage(thresholdMs) {
                return __('You have %s left to finish the test', humanizeTime(thresholdMs / 1000));
            }
        },
        testPart: {
            getMessage(thresholdMs) {
                return __('You have %s left to finish this part', humanizeTime(thresholdMs / 1000));
            }
        },
        section: {
            getMessage(thresholdMs) {
                return __('You have %s left to finish this section', humanizeTime(thresholdMs / 1000));
            }
        },
        item: {
            getMessage(thresholdMs) {
                return __('You have %s left to finish this item', humanizeTime(thresholdMs / 1000));
            }
        }
    }
};

/**
 * Normalize the warning configuration passed to the timers service.
 * @param {Object} [warningConfig]
 * @returns {Object}
 */
function normalizeWarningConfig(warningConfig = {}) {
    const normalized = cloneDeep(defaultWarningConfig);

    warningConfig.levels ??= {};
    for (const key of Object.keys(warningConfig.levels)) {
        let thresholdsInMs = warningConfig.levels[key]?.thresholdsInMs;

        if (!Array.isArray(thresholdsInMs)) {
            thresholdsInMs = [thresholdsInMs];
        }

        normalized.levels[key] ??= {};
        normalized.levels[key].thresholdsInMs = thresholdsInMs
            .map(value => Number(value))
            .filter(value => Number.isFinite(value) && value > 0)
            .sort((a, b) => a - b);
    }

    // Backward compatibility: when only the legacy test-level threshold is configured,
    // reuse it for testPart/section/item warnings. Level-specific thresholds do not
    // propagate to other levels.
    const testThresholds = normalized.levels[DEFAULT_WARNING_LEVEL]?.thresholdsInMs || [];
    if (testThresholds.length) {
        warningLevels.forEach(level => {
            if (!normalized.levels[level]?.thresholdsInMs?.length) {
                normalized.levels[level] ??= {};
                normalized.levels[level].thresholdsInMs = [...testThresholds];
            }
        });
    }

    if (typeof warningConfig.notificationTimeout === 'number') {
        normalized.notificationTimeout = Math.max(0, warningConfig.notificationTimeout);
    }

    return normalized;
}

function getTimerKey(level, id) {
    return `${level}:${id || 'global'}`;
}

function getWarningThresholds(warningConfig, timerLevel) {
    return warningConfig.levels[timerLevel]?.thresholdsInMs || [];
}

function getExtraApplicableTimerKey(timersArray, activeTimerKeys) {
    const extraTimer = timersArray.find(timerData => timerData.level === 'extra');
    if (!extraTimer?.timerValue || extraTimer.timerValue.timeLeft <= 0) {
        return null;
    }

    const activeTimers = visibleTimerLevels
        .map(level =>
            timersArray.find(
                timerData => timerData.level === level && activeTimerKeys.has(getTimerKey(level, timerData.id))
            )
        )
        .filter(Boolean);

    if (!activeTimers.length) {
        return null;
    }

    const minTimeLeft = Math.min(...activeTimers.map(timerData => timerData.timerValue.timeLeft));
    const extraApplicableTimer = activeTimers.filter(timerData => timerData.timerValue.timeLeft === minTimeLeft).pop();

    return extraApplicableTimer ? getTimerKey(extraApplicableTimer.level, extraApplicableTimer.id) : null;
}

function getTimerWarningMessage(payload, getTimerWarningMessageCallback) {
    const message =
        typeof getTimerWarningMessageCallback === 'function' ? getTimerWarningMessageCallback(payload) : void 0;

    return message || defaultWarningMessages.levels[payload.timer.level]?.getMessage?.(payload.thresholdMs);
}

function getShownWarningKey(level, id, thresholdMs) {
    return `${getTimerKey(level, id)}:${thresholdMs}`;
}

/**
 * Creates timersService object with methods to control timers
 * @param {Object} socketProxy
 * @param {Object} config
 * @param {String} config.mode
 * @param {String} config.serviceCallId - identifier used by the local store
 * @param {Function} config.onError - callback to propagate errors to consumer
 * @param {Function} [config.onTimerTimeout] - callback to propagate timeouts to consumer
 * @param {Function} [config.onTimerWarning] - callback to propagate warning-threshold crossings to consumer
 * @param {Function} [config.onExtraAdded] - callback when extra-time is added: it will resume timers that were timed-out earlier
 * @param {Function} [config.getTimerWarningMessage] - callback used to resolve warning notification text
 * @param {Object} [config.throttleConfig] - configuration for how often to notify about timer updates
 * @param {Object} [config.warningConfig] - configuration for pre-timeout warning notifications
 * @returns {Object} API
 */
export function timersServiceFactory(socketProxy, config) {
    if (!config.serviceCallId) {
        throw new TypeError('config.serviceCallId must be provided');
    }
    if (!config.mode || !(config.mode in timerModes)) {
        throw new TypeError('config.mode must be a valid timers mode');
    }

    const store = getTimersStore(config.serviceCallId, {
        throttleConfig: config.throttleConfig
    });
    const warningConfig = normalizeWarningConfig(config.warningConfig);
    const activeWarningTimerKeys = new Set([TEST_TIMER_KEY]);
    const shownWarningKeys = new Set();
    let prevActiveWarningTimerKeys = new Set([TEST_TIMER_KEY]);
    let useInitialWarningStateOnNextRefresh = false;
    let isProcessingFirstRefreshAfterInitialization = false;

    const seedActiveWarningTimers = () => {
        activeWarningTimerKeys.clear();
        activeWarningTimerKeys.add(TEST_TIMER_KEY);
    };

    const resetActiveWarningTimers = () => {
        prevActiveWarningTimerKeys = new Set([TEST_TIMER_KEY]);
        seedActiveWarningTimers();
    };

    const setActiveWarningTimers = timersData => {
        const previousActiveWarningTimerKeys = new Set(activeWarningTimerKeys);
        let hasActiveExtraTimer = false;
        let hasVisibleNonTestTimer = false;

        prevActiveWarningTimerKeys = previousActiveWarningTimerKeys;
        seedActiveWarningTimers();
        timersData.forEach(timerData => {
            if (timerData.level === 'extra') {
                hasActiveExtraTimer = timerData.timerValue?.timeLeft > 0;
                return;
            }

            if (timerData.level !== DEFAULT_WARNING_LEVEL) {
                hasVisibleNonTestTimer = true;
            }
            activeWarningTimerKeys.add(getTimerKey(timerData.level, timerData.id));
        });

        if (hasActiveExtraTimer && !hasVisibleNonTestTimer) {
            previousActiveWarningTimerKeys.forEach(timerKey => {
                if (timerKey !== TEST_TIMER_KEY) {
                    activeWarningTimerKeys.add(timerKey);
                }
            });
        }
    };

    const isTimerActiveForWarnings = timerData =>
        activeWarningTimerKeys.has(getTimerKey(timerData.level, timerData.id));

    // subscribe to changes in order to report whenever timers time out
    let prevTimersArray = [];
    const unsubscribe = store.subscribe(stored => {
        /** @type {TimerData[]} */
        let timedOutTimers = [];

        const prevExtra = prevTimersArray.find(t => t.level === 'extra'); //if `setInitialData` executed, it will always exist
        const currentExtra = stored.timersArray.find(t => t.level === 'extra');
        const prevWarningExtraTimerKey = getExtraApplicableTimerKey(prevTimersArray, prevActiveWarningTimerKeys);
        const currentWarningExtraTimerKey = getExtraApplicableTimerKey(stored.timersArray, activeWarningTimerKeys);
        if (prevExtra && prevExtra.timerValue.timeLeft <= 0 && currentExtra && currentExtra.timerValue.timeLeft > 0) {
            if (typeof config.onExtraAdded === 'function') {
                config.onExtraAdded();
            }
        } else {
            for (const timerData of stored.timersArray) {
                if (timerData.level !== 'extra') {
                    const prevTimerData = prevTimersArray.find(
                        t => t.level === timerData.level && (!timerData.id || t.id === timerData.id)
                    );
                    const prevEffectiveTimeLeft = prevTimerData
                        ? prevTimerData.timerValue.timeLeft + (prevExtra ? prevExtra.timerValue.timeLeft : 0)
                        : Infinity;
                    const currentEffectiveTimeLeft =
                        timerData.timerValue.timeLeft + (currentExtra ? currentExtra.timerValue.timeLeft : 0);

                    const wasTimedOut = prevEffectiveTimeLeft <= 0;
                    const isTimedOut = currentEffectiveTimeLeft <= 0;

                    if (prevTimerData && !wasTimedOut && isTimedOut) {
                        timedOutTimers.push(timerData);
                    }

                    const warningThresholds = getWarningThresholds(warningConfig, timerData.level);
                    if (!warningThresholds.length || !isTimerActiveForWarnings(timerData)) {
                        continue;
                    }

                    const timerKey = getTimerKey(timerData.level, timerData.id);
                    const prevWarningEffectiveTimeLeft = prevTimerData
                        ? prevTimerData.timerValue.timeLeft +
                          (prevWarningExtraTimerKey === timerKey ? prevExtra?.timerValue.timeLeft || 0 : 0)
                        : Infinity;
                    const currentWarningEffectiveTimeLeft =
                        timerData.timerValue.timeLeft +
                        (currentWarningExtraTimerKey === timerKey ? currentExtra?.timerValue.timeLeft || 0 : 0);
                    const shouldUsePreviousStoredTime =
                        prevTimerData &&
                        (prevActiveWarningTimerKeys.has(timerKey) || isProcessingFirstRefreshAfterInitialization);
                    const prevWarningTimeLeft = shouldUsePreviousStoredTime ? prevWarningEffectiveTimeLeft : Infinity;

                    for (const thresholdInMs of warningThresholds) {
                        const shownWarningKey = getShownWarningKey(timerData.level, timerData.id, thresholdInMs);
                        const wasAboveThreshold = prevTimerData && prevWarningTimeLeft > thresholdInMs;
                        const isAtOrBelowThreshold = currentWarningEffectiveTimeLeft <= thresholdInMs;
                        const hasRemainingTime = currentWarningEffectiveTimeLeft > 0;

                        if (currentWarningEffectiveTimeLeft > thresholdInMs) {
                            shownWarningKeys.delete(shownWarningKey);
                        }

                        if (shownWarningKeys.has(shownWarningKey)) {
                            continue;
                        }

                        if (wasAboveThreshold && isAtOrBelowThreshold && hasRemainingTime) {
                            const payload = {
                                thresholdMs: thresholdInMs,
                                timer: cloneDeep(timerData),
                                effectiveTimeLeft: currentWarningEffectiveTimeLeft
                            };

                            notifyTimerWarning(
                                getTimerWarningMessage(payload, config.getTimerWarningMessage),
                                warningConfig.notificationTimeout
                            );

                            if (typeof config.onTimerWarning === 'function') {
                                config.onTimerWarning(payload);
                            }

                            shownWarningKeys.add(shownWarningKey);

                            // only notify about the first (=lowest) matching threshold value
                            // (because proctor extraTime deduction could cause multiple thresholds to be passed):
                            break;
                        }
                    }
                }
            }
            if (timedOutTimers.length) {
                if (typeof config.onTimerTimeout === 'function') {
                    config.onTimerTimeout(); // details will be checked from runner side, where we have testContext
                }
            }
        }

        prevTimersArray = cloneDeep(stored.timersArray);
    });

    // just create a timersProxy: need to open socket connection using API
    const timersProxy = timersProxyFactory(socketProxy, {
        mode: config.mode,
        onError: config.onError
    });

    timersProxy.on('refresh-timers', msg => {
        const timersData = [];

        if (msg.test) {
            timersData.push({
                level: 'test',
                timerValue: {
                    timeLeft: msg.test.maxTimeRemaining,
                    timeAssigned: msg.test.maxTime
                }
            });
        }
        if (msg.testPart) {
            timersData.push({
                level: 'testPart',
                id: msg.testPart.id,
                timerValue: {
                    timeLeft: msg.testPart.maxTimeRemaining,
                    timeAssigned: msg.testPart.maxTime
                }
            });
        }
        if (msg.section) {
            timersData.push({
                level: 'section',
                id: msg.section.id,
                timerValue: {
                    timeLeft: msg.section.maxTimeRemaining,
                    timeAssigned: msg.section.maxTime
                }
            });
        }
        if (msg.item) {
            timersData.push({
                level: 'item',
                id: msg.item.id,
                timerValue: {
                    minTime: msg.item.minTime,
                    timeLeft: msg.item.maxTimeRemaining,
                    timeAssigned: msg.item.maxTime
                }
            });
        }
        if (msg.extra) {
            timersData.push({
                level: 'extra',
                timerValue: {
                    timeLeft: msg.extra.maxTimeRemaining,
                    timeAssigned: msg.extra.maxTime
                }
            });
        } else {
            timersData.push({
                level: 'extra',
                timerValue: {
                    timeLeft: 0,
                    timeAssigned: 0
                }
            });
        }

        isProcessingFirstRefreshAfterInitialization = useInitialWarningStateOnNextRefresh;
        setActiveWarningTimers(timersData);
        store.updateTimeLeft(timersData);
        isProcessingFirstRefreshAfterInitialization = false;
        useInitialWarningStateOnNextRefresh = false;
    });

    const service = {
        mode: config.mode,

        /**
         * Transform the timer definitions object received from server on /init
         * and set the result into a store
         * @param {Object} initialTimersData
         * @param {Object} [initialTimersData.test]
         * @param {Object[]} [initialTimersData.testParts]
         * @param {Object[]} [initialTimersData.sections]
         * @param {Object[]} [initialTimersData.items]
         * @param {Object} [initialTimersData.extra]
         */
        setInitialData(initialTimersData) {
            resetActiveWarningTimers();
            shownWarningKeys.clear();
            useInitialWarningStateOnNextRefresh = true;
            store.initializeTimers(buildInitialTimersStoreData(initialTimersData));
        },

        //call when item is rendered
        start(testContext) {
            timersProxy.start(testContext);
        },

        //call when item is unloaded
        stop() {
            timersProxy.stop();
        },

        /**
         * @returns {Observable<*>}
         */
        getStore() {
            return store;
        },

        /**
         * Call at the end of the test session to disconnect socket and remove local store subscription
         */
        destroy() {
            unsubscribe();
            store.clear();
        }
    };

    return service;
}

function notifyTimerWarning(message, notificationTimeout) {
    if (!message) {
        return;
    }

    showNotification(
        {
            title: message,
            hierarchy: 'warning',
            closeable: true
        },
        'autoclose',
        notificationTimeout
    );
}
